import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Play, Radio, Loader2, AlertTriangle, Film } from "lucide-react";
import api from "@/lib/api";
import HlsPlayer from "@/components/HlsPlayer";
import { ShareButtons } from "@/components/ShareButtons";

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  catch { return ""; }
};

// Stable anonymous viewer id for analytics (no PII).
const viewerSid = () => {
  let sid = localStorage.getItem("sw_viewer_sid");
  if (!sid) {
    sid = crypto.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2);
    localStorage.setItem("sw_viewer_sid", sid);
  }
  return sid;
};

export default function EmbedLibrary() {
  const { ownerId } = useParams();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selected, setSelected] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [visible, setVisible] = useState(10);
  const [activeTab, setActiveTab] = useState("all");
  const didInitSelect = useRef(false);
  const channelCode = params.get("code") || null;

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // Log a play event every time a viewer starts a video or live stream.
  // Only the video a shared link pointed to is attributed to that share source.
  const shareRef = params.get("ref");
  const sharedVideo = params.get("v");
  const logView = (item) => {
    if (!item?.playback_url) return;
    const fromShare = shareRef && !item.is_live && item.id === sharedVideo;
    api.post(`/analytics/view`, {
      source: fromShare ? shareRef : null,
      owner_id: ownerId,
      channel_code: channelCode,
      video_id: item.is_live ? null : item.id,
      stream_id: item.is_live ? item.id : null,
      kind: item.is_live ? "stream" : "video",
      session_id: viewerSid(),
    }).catch(() => {});
  };

  const playItem = (item) => {
    setSelected(item);
    logView(item);
  };

  const fetchData = async () => {
    try {
      const pl = params.get("playlist");
      const r = pl
        ? await api.get(`/public/playlist/${pl}`)
        : await api.get(`/public/library/${ownerId}`);
      setData(r.data);
      if (!didInitSelect.current) {
        didInitSelect.current = true;
        const wanted = params.get("v");
        const first = (wanted && r.data.videos?.find((v) => v.id === wanted)) || (r.data.live?.[0]) || (r.data.videos?.[0]) || null;
        setSelected(first);
        if (first) logView(first);
      }
      setError(false);
    } catch (e) {
      setError(e?.response?.status === 403 ? "paused" : true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const t = setInterval(fetchData, 20000); // pick up "going live" without reload
    return () => clearInterval(t);
  }, [ownerId]);

  if (loading) return <div className="fixed inset-0 bg-white flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" style={{ color: params.get("accent") ? `#${params.get("accent")}` : "#F59E0B" }} /></div>;
  if (error || !data) {
    return (
      <div className="fixed inset-0 bg-white flex flex-col items-center justify-center text-center px-6">
        <AlertTriangle className="h-8 w-8 text-amber-500" />
        <p data-testid="embed-unavailable" className="mt-3 text-gray-900 font-medium">{error === "paused" ? "Temporarily unavailable" : "Library unavailable"}</p>
        {error === "paused" && <p className="text-gray-500 text-sm mt-1">This church's videos are paused right now. Please check back soon.</p>}
      </div>
    );
  }

  const org = data.org || {};
  const qAccent = params.get("accent");
  const qText = params.get("text");
  const accent = qAccent ? `#${qAccent.replace("#", "")}` : (org.accent_color || "#F59E0B");
  const barText = qText ? `#${qText.replace("#", "")}` : "#0B0F17";
  const title = params.get("title") || org.organization || data.name || "Video Library";
  const logo = params.get("logo") || org.logo_url || "";
  const showBar = params.get("bar") !== "0";
  const liveNow = data.live?.[0] || null;
  const isWatchingLive = selected?.is_live;
  const videosSorted = [...(data.videos || [])].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  const playlists = data.playlists || [];
  const activePlaylist = playlists.find((p) => p.id === activeTab) || null;
  const baseItems = [...(data.live || []), ...videosSorted];
  const allItems = activePlaylist
    ? activePlaylist.video_ids.map((vid) => videosSorted.find((v) => v.id === vid)).filter(Boolean)
    : baseItems;
  const visibleItems = allItems.slice(0, visible);
  const hasMore = allItems.length > visible;
  const selectTab = (id) => { setActiveTab(id); setVisible(10); };

  // Next-service countdown (hidden while live, or when the bar is hidden for a bare player).
  const SOON_MS = 5 * 60 * 1000;
  const ns = data.next_service || null;
  const nsLabel = ns?.label || "";
  const nsAt = ns?.at ? new Date(ns.at).getTime() : 0;
  const nsLeft = nsAt ? nsAt - now : 0;
  const showCountdown = showBar && !liveNow && nsAt > 0 && nsLeft > 0;
  const startingSoon = showCountdown && nsLeft <= SOON_MS;
  const fmtCountdown = (ms) => {
    const t = Math.max(0, Math.floor(ms / 1000));
    const d = Math.floor(t / 86400), h = Math.floor((t % 86400) / 3600), m = Math.floor((t % 3600) / 60), sec = t % 60;
    const pad = (n) => String(n).padStart(2, "0");
    return d > 0 ? `${d}d ${pad(h)}:${pad(m)}:${pad(sec)}` : `${pad(h)}:${pad(m)}:${pad(sec)}`;
  };
  const fmtWhen = (iso) => {
    try { return new Date(iso).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
    catch { return ""; }
  };

  return (
    <div data-testid="library-layout" className="h-[100dvh] overflow-hidden bg-white text-gray-900 flex flex-col">
      {/* Branding bar (hidden when ?bar=0) */}
      {showBar && (
        <div data-testid="library-bar" className="shrink-0 flex items-center gap-2 px-4 py-2.5 text-sm font-semibold" style={{ background: accent, color: barText }}>
          {logo ? <img src={logo} alt="logo" className="h-5 object-contain" /> : <Radio className="h-4 w-4" />}
          {title}
        </div>
      )}

      {/* Next service countdown strip */}
      {showCountdown && (
        <div data-testid="library-countdown" className="shrink-0 flex items-center justify-center gap-2.5 px-4 py-2 text-sm bg-gray-50 border-b border-gray-200">
          {startingSoon ? (
            <span className="inline-flex items-center gap-2 font-semibold" style={{ color: accent }}>
              <span className="h-2 w-2 rounded-full animate-pulse" style={{ backgroundColor: accent }} />
              {nsLabel || "Service"} starting soon…
            </span>
          ) : (
            <>
              <span className="text-gray-500">{nsLabel || "Next service"} in</span>
              <span className="font-display font-bold tabular-nums" style={{ color: accent }}>{fmtCountdown(nsLeft)}</span>
              <span className="text-gray-400 hidden sm:inline">· {fmtWhen(ns.at)}</span>
            </>
          )}
        </div>
      )}

      {/* Live now banner */}
      {liveNow && (
        <div data-testid="live-now-banner" className="shrink-0 flex items-center gap-3 px-4 py-2.5 bg-red-600 text-white text-sm">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-white" />
          </span>
          <span className="font-bold tracking-wide">LIVE NOW</span>
          <span className="truncate text-white/90">{liveNow.title}</span>
          {!isWatchingLive && (
            <button data-testid="watch-live-btn" onClick={() => playItem(liveNow)} className="ml-auto shrink-0 bg-white text-red-600 font-semibold text-xs px-3 py-1.5 rounded-lg hover:bg-white/90 transition-colors">
              Watch live →
            </button>
          )}
        </div>
      )}

      {/* Full-width video player at the top */}
      <div data-testid="library-player" className="shrink-0 w-full bg-black">
        {selected?.playback_url ? (
          <div className="relative w-full aspect-video max-h-[55dvh] mx-auto">
            {selected.is_live && (
              <span data-testid="player-live-tag" className="absolute top-3 left-3 z-10 inline-flex items-center gap-1.5 bg-red-600 text-white text-[11px] font-bold px-2 py-1 rounded">
                <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" /> LIVE
              </span>
            )}
            <HlsPlayer key={selected.id} src={selected.playback_url} poster={selected.thumbnail_url} autoPlay controls live={!!selected.is_live} />
          </div>
        ) : (
          <div className="w-full aspect-video max-h-[55dvh] flex items-center justify-center">
            <div className="text-center">
              <Film className="h-10 w-10 text-gray-600 mx-auto" />
              <p className="text-gray-500 mt-3 text-sm">Select a video to play</p>
            </div>
          </div>
        )}
      </div>

      {selected && data.share?.allow && (
        <div data-testid="library-share-bar" className="shrink-0 flex items-center gap-3 px-4 sm:px-6 py-2 border-b border-gray-100">
          <p className="text-sm font-medium text-gray-800 truncate flex-1">{selected.title}</p>
          <ShareButtons settings={data.share} dark={false}
            target={selected.is_live ? { streamId: selected.id } : { ownerId, videoId: selected.id }}
            title={org.organization ? `${selected.title} — ${org.organization}` : selected.title} />
        </div>
      )}

      {/* Archive grid — scrolls independently below the fixed player */}
      <div data-testid="library-scroll" className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {allItems.length === 0 ? (
          <p className="text-gray-500 text-sm py-16 text-center">No videos published yet.</p>
        ) : (
          <>
            {playlists.length > 0 && (
              <div data-testid="library-tabs" className="flex flex-wrap gap-2 mb-5">
                <button
                  data-testid="library-tab-all"
                  onClick={() => selectTab("all")}
                  className={"px-4 py-1.5 rounded-full text-xs font-semibold transition-colors " + (activeTab === "all" ? "text-black" : "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200")}
                  style={activeTab === "all" ? { backgroundColor: accent } : {}}
                >
                  All
                </button>
                {playlists.map((p) => (
                  <button
                    key={p.id}
                    data-testid={`library-tab-${p.id}`}
                    onClick={() => selectTab(p.id)}
                    className={"px-4 py-1.5 rounded-full text-xs font-semibold transition-colors " + (activeTab === p.id ? "text-black" : "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200")}
                    style={activeTab === p.id ? { backgroundColor: accent } : {}}
                  >
                    {p.name}
                  </button>
                ))}
              </div>
            )}
            <h2 className="text-sm font-semibold text-gray-700 mb-4 tracking-wide">{activePlaylist ? activePlaylist.name : "Recent Videos"}</h2>
            <div data-testid="library-grid" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {visibleItems.map((v) => (
                <button
                  key={v.id}
                  data-testid={`library-item-${v.id}`}
                  onClick={() => playItem(v)}
                  className={"group text-left rounded-xl overflow-hidden bg-white border transition-all shadow-sm hover:shadow-md hover:bg-gray-50 " + (selected?.id === v.id ? "border-transparent" : "border-gray-200 hover:border-gray-300")}
                  style={selected?.id === v.id ? { boxShadow: `0 0 0 2px ${accent}` } : {}}
                >
                  <div className="relative aspect-video bg-gray-900 flex items-center justify-center overflow-hidden">
                    {v.thumbnail_url ? <img src={v.thumbnail_url} alt="" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" /> : <Play className="h-6 w-6 text-gray-600" />}
                    {v.is_live && <span className="absolute top-2 left-2 text-[9px] font-bold px-1.5 py-0.5 rounded bg-red-600 text-white">LIVE</span>}
                    <span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/30">
                      <span className="h-10 w-10 rounded-full flex items-center justify-center" style={{ backgroundColor: accent }}>
                        <Play className="h-4 w-4 text-black translate-x-[1px]" fill="currentColor" />
                      </span>
                    </span>
                  </div>
                  <div className="p-3">
                    <p className="text-sm font-medium line-clamp-2 leading-snug">{v.title}</p>
                    {v.created_at && <p className="text-[11px] text-gray-500 mt-1.5">{fmtDate(v.created_at)}</p>}
                  </div>
                </button>
              ))}
            </div>
            {hasMore && (
              <div className="flex justify-center mt-8">
                <button data-testid="library-load-more" onClick={() => setVisible((n) => n + 10)} className="inline-flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold border border-gray-300 text-gray-800 hover:bg-gray-100 transition-colors">
                  Load More
                </button>
              </div>
            )}
          </>
        )}
      </div>
      </div>
    </div>
  );
}
